/* eslint-disable react-hooks/exhaustive-deps */
import { usePathname } from "next/navigation";
import { FC, useEffect, useRef } from "react";

declare global {
  interface Window {
    chatwootSettings?: chatwootSettings;
    $chatwoot?: Chatwoot;
    chatwootSDK?: ChatwootSDK;
  }
}

interface ChatwootProps {
  user?: User;
}

const ChatwootIntegration: FC<ChatwootProps> = ({ user }) => {
  const pathName = usePathname();
  const pathRef = useRef(pathName);
  const userRef = useRef(user);

  useEffect(() => {
    pathRef.current = pathName;
  }, [pathName]);

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  useEffect(() => {
    const updateVisibility = () => {
      const currentPath = pathRef.current;
      const isOnChatPage =
        currentPath.startsWith("/chat") ||
        currentPath.startsWith("/voice-chat") ||
        currentPath.startsWith("/content");

      window.$chatwoot?.toggleBubbleVisibility(isOnChatPage ? "hide" : "show");
    };

    if (window.$chatwoot?.hasLoaded) {
      updateVisibility();
    }

    window.addEventListener("chatwoot:ready", updateVisibility);
    window.addEventListener("chatwoot:close", updateVisibility);

    return () => {
      window.removeEventListener("chatwoot:ready", updateVisibility);
      window.removeEventListener("chatwoot:close", updateVisibility);
    };
  }, [pathName]);

  useEffect(() => {
    if (!user?.id) return;

    const setUserIdentity = () => {
      const currentUser = userRef.current;
      if (currentUser?.id) {
        window.$chatwoot?.setUser(currentUser.id, {
          name: currentUser.name,
          email: currentUser.email,
          avatar_url: currentUser.image,
          custom_attributes: {
            learning_language: currentUser.learning_language,
            coach_name: currentUser.coach?.name,
          },
        });
      }
    };

    if (window.$chatwoot?.hasLoaded) {
      setUserIdentity();
    }

    window.addEventListener("chatwoot:ready", setUserIdentity, { once: true });

    return () => {
      window.removeEventListener("chatwoot:ready", setUserIdentity);
    };
  }, [
    user?.id,
    user?.name,
    user?.email,
    user?.image,
    user?.learning_language,
    user?.coach?.name,
  ]);

  useEffect(() => {
    const BASE_URL = process.env.NEXT_PUBLIC_DEV_CHATWOOT_BASE_URL;
    const websiteToken = process.env.NEXT_PUBLIC_CHATWOOT_WEBSITE_TOKEN;

    if (!BASE_URL || !BASE_URL.startsWith("https://")) {
      throw new Error("Invalid Chatwoot BASE_URL");
    }

    window.chatwootSettings = {
      hideMessageBubble: false,
      position: "left",
      locale: "en",
      type: "standard",
      darkMode: "auto",
    };

    if (!window.$chatwoot && !window.chatwootSDK) {
      const existingScript = document.querySelector<HTMLScriptElement>(
        `script[src="${BASE_URL}/packs/js/sdk.js"]`,
      );

      if (!existingScript) {
        const newScript = document.createElement("script");
        const firstScript = document.getElementsByTagName("script")[0];
        newScript.src = `${BASE_URL}/packs/js/sdk.js`;
        newScript.defer = true;
        newScript.async = true;
        firstScript.parentNode?.insertBefore(newScript, firstScript);

        newScript.onload = () => {
          window.chatwootSDK?.run({
            websiteToken: websiteToken as string,
            baseUrl: BASE_URL as string,
          });
        };
      }
    }
  }, []);

  return null;
};

export default ChatwootIntegration;
